/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { researchTree } from '../../data/researchTree';
import { TabContainer, Tab } from '../ui';

export function ResearchViewStatic() {
  const [activeTab, setActiveTab] = useState<'overview' | 'technology' | 'design' | 'business'>('overview');

  // Static data - no state dependencies
  const allNodes = researchTree.branches.flatMap(branch => branch.nodes);

  // Group nodes by category for display
  const nodesByCategory = {
    technology: allNodes.filter(node => node.category === 'technology'),
    design: allNodes.filter(node => node.category === 'design'),
    business: allNodes.filter(node => node.category === 'business'),
  };

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'technology', label: `Technology (${nodesByCategory.technology.length})` },
    { id: 'design', label: `Design (${nodesByCategory.design.length})` },
    { id: 'business', label: `Business (${nodesByCategory.business.length})` },
  ];

  return (
    <TabContainer tabs={tabs} activeTab={activeTab} onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}>
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="bg-theme-bg-content rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-6">Research & Development</h2>

            {/* Static Info Panel */}
            <div className="bg-theme-info/10 border border-theme-info/30 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-theme-info mb-2">Research System (Static View)</h3>
              <p className="text-sm text-theme-info">
                This is a static view of the research tree. All nodes are shown in their locked state.
              </p>
              <p className="text-sm text-theme-info/80 mt-2">
                Research functionality is temporarily unavailable.
              </p>
            </div>

            {/* Static Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-theme-bg-tertiary rounded-lg p-4">
                <div className="text-sm text-theme-text-secondary">Research Points</div>
                <div className="text-2xl font-bold text-theme-text-primary">0</div>
              </div>
              <div className="bg-theme-bg-tertiary rounded-lg p-4">
                <div className="text-sm text-theme-text-secondary">Unlocked Technologies</div>
                <div className="text-2xl font-bold text-theme-text-primary">0 / {allNodes.length}</div>
              </div>
              <div className="bg-theme-bg-tertiary rounded-lg p-4">
                <div className="text-sm text-theme-text-secondary">Monthly Investment</div>
                <div className="text-2xl font-bold text-theme-text-primary">$0</div>
              </div>
            </div>

            {/* Footer Info */}
            <div className="p-4 bg-theme-bg-secondary rounded-lg">
              <p className="text-sm text-theme-text-secondary">
                Total Research Nodes: {allNodes.length} |
                Categories: Technology, Design, Business |
                Status: All Locked
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'technology' && (
        <div className="space-y-6">
          <div className="bg-theme-bg-content rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-6">Technology Research</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {nodesByCategory.technology.map(node => (
                <NodeCard key={node.id} node={node} isUnlocked={false} />
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'design' && (
        <div className="space-y-6">
          <div className="bg-theme-bg-content rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-6">Design Research</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {nodesByCategory.design.map(node => (
                <NodeCard key={node.id} node={node} isUnlocked={false} />
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'business' && (
        <div className="space-y-6">
          <div className="bg-theme-bg-content rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-6">Business Research</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {nodesByCategory.business.map(node => (
                <NodeCard key={node.id} node={node} isUnlocked={false} />
              ))}
            </div>
          </div>
        </div>
      )}
    </TabContainer>
  );
}

// Static Node Card Component
function NodeCard({ node, isUnlocked }: { node: any; isUnlocked: boolean }) {
  return (
    <div className={`
      p-3 rounded-lg border-2 transition-all
      ${isUnlocked
        ? 'bg-theme-success/10 border-theme-success shadow-sm'
        : 'bg-theme-bg-secondary border-theme-border opacity-75'
      }
    `}>
      <div className="flex items-start justify-between mb-1">
        <h4 className={`text-sm font-medium ${isUnlocked ? 'text-theme-success' : 'text-theme-text-secondary'}`}>
          {node.name}
        </h4>
        {isUnlocked ? (
          <span className="text-xs bg-theme-success text-white px-2 py-0.5 rounded">
            Unlocked
          </span>
        ) : (
          <span className="text-xs bg-theme-text-muted text-white px-2 py-0.5 rounded">
            Locked
          </span>
        )}
      </div>

      <p className="text-xs text-theme-text-secondary mb-2 line-clamp-2">
        {node.description}
      </p>

      <div className="flex items-center justify-between text-xs">
        <span className="text-theme-text-muted">Cost: {node.cost} RP</span>
        <span className="text-theme-text-muted">Tier {node.tier}</span>
      </div>

      {/* Prerequisites */}
      {node.prerequisites && node.prerequisites.length > 0 && (
        <div className="mt-2 pt-2 border-t border-theme-border">
          <span className="text-xs text-theme-text-muted">
            Requires: {node.prerequisites.length} prerequisite(s)
          </span>
        </div>
      )}
    </div>
  );
}
